import { isNotDefined } from '@sgme/fp';
import { memo, useEffect } from 'react';

import { useCallOrderTileId } from '@/App/tiles/orders/CallOrder/CallOrderCreationTile/CallOrderContext.tsx';
import { CallOrderCreationTile } from '@/App/tiles/orders/CallOrder/CallOrderCreationTile/index.tsx';
import { CallOrderEditingTile } from '@/App/tiles/orders/CallOrder/CallOrderEditingTile/index.tsx';
import CallOrderExecutedTile from '@/App/tiles/orders/CallOrder/CallOrderExecutedTile';
import { ExecutionPendingOrderStatus } from '@/App/tiles/orders/ExecutionPendingOrderStatus.tsx';
import { ErrorTile } from '@/App/tiles/shared/ErrorTile.tsx';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { selectCallOrderTile } from '@/store/orders/call/selectors.ts';
import { ordersSlice } from '@/store/orders/slice.ts';
import { tilesSlice } from '@/store/tiles/slice.ts';
import { deepEqual } from '@/utils/object.ts';

/**
 * Memoized CallOrderTile component because the Grid rerenders a lot.
 */
export const CallOrderTile = memo(() => {
  const tileId = useCallOrderTileId();
  const dispatch = useAppDispatch();
  const userPreferences = useAppSelector((state) => state.userPreferences);

  const { status, mode, errorCode } = useAppSelector((state) => {
    const tile = selectCallOrderTile(state, tileId);

    if (isNotDefined(tile)) {
      return {
        status: 'creating',
      };
    }

    return {
      status: tile.properties.status.type,
      mode: tile.properties.mode,
      errorCode: tile.properties.status.type === 'failed' ? tile.properties.status.errorMessage : undefined,
    };
  }, deepEqual);

  const tileWidth = useAppSelector((state) => tilesSlice.selectors.selectTileW(state, tileId));

  useEffect(() => {
    if (tileWidth !== 1) {
      dispatch(tilesSlice.actions.resizeTile({ tileId, w: 1 }));
    }
  }, [tileId, tileWidth]);

  if (status === 'pending') {
    return <ExecutionPendingOrderStatus isEditing={mode === 'EDIT'} />;
  }

  if (status === 'failed') {
    const handleReset = () => {
      dispatch(ordersSlice.actions.resetOrder({ tileId, userPreferences }));
    };

    return (
      <ErrorTile
        tileId={tileId}
        onReset={handleReset}
        errorCode={errorCode}
        quoteId={tileId}
        errorDescription={
          errorCode === 'order.timeout' ? 'order.timeout.error.message.description' : 'order.error.message.description'
        }
        errorTitle={errorCode === 'order.timeout' ? 'order.timeout.error.message.title' : 'order.error.message.title'}
      />
    );
  }

  if (mode === 'EDIT') {
    return <CallOrderEditingTile />;
  }

  if (status === 'creating') {
    return <CallOrderCreationTile />;
  }

  return <CallOrderExecutedTile />;
});
